import React from 'react'
import { Link } from 'gatsby'
import {
  Layout,
  Showcase,
  SectionTitle,
  Header,
  Intro,
} from '../../../../components'
import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      />
      <Layout
        inProgress={true}
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      >
        <Intro>
          <p>
            Tooltips are elevated labels that provide brief, supplementary
            information about, or identify, the element they point to. They do
            not support rich text or complex operations.
          </p>
          <p>
            Tooltips hide information by default and should be used sparingly.
            If the user needs to know the information within the tooltip to
            complete their task, place it within the main layout. Additionally,
            tooltip content should be unique and not repeat information that
            already exists on the page.
          </p>
        </Intro>
        <SectionTitle>Variants</SectionTitle>
        <p>
          The only required element in a tooltip is body content. Depending on
          the variant, a Close action will be included. Tooltips can include
          an optional{' '}
          <Link class="link" to={'/' + data.site + '/components/title/design'}>
            Title
          </Link>{' '}
          for increased scannability.
        </p>
        <h3>Default</h3>

        <Showcase
          site={data.site}
          title="Default"
          text="The Default Tooltip is the most accessible tooltip option and is triggered on tap, click or keyboard focus. It persists on screen, no matter mouse position, until it is expressly dismissed by selecting the Close action or moving focus out of the tooltip. This tooltip should be used in most cases."
        >
          <div class="c-tooltip c-tooltip--top">
            <div class="c-tooltip__header">
              <h1 class="c-tooltip__title">Title</h1>
              <button class="c-tooltip__close-button">
                Close{' '}
                <i class="c-tooltip__close-button-icon icon icon--close"></i>
              </button>
            </div>
            <div class="c-tooltip__content">
              <p>
                I provide additional information that is nice to have but that
                you don't need to complete your task.
              </p>
              <p>You must click, tap or tab to open me.</p>
            </div>
          </div>
        </Showcase>
        <h3>Hover</h3>
        <Showcase
          site={data.site}
          title="Hover"
          text="The Hover Tooltip is triggered on mouseover and should only be used at larger, non-touchscreen breakpoints. Outside of these environments, revert to a default tooltip."
        >
          <div class="c-tooltip c-tooltip--top">
            <div class="c-tooltip__header">
              <h1 class="c-tooltip__title">Title</h1>
            </div>
            <div class="c-tooltip__content">
              <p>
                I provide additional information that is nice to have but that
                you don't need to complete your task.
              </p>
              <p>You can open me on mouseover and close me on mouse away.</p>
            </div>
          </div>
        </Showcase>
        <h3>Icon</h3>
        <Showcase
          site={data.site}
          title="Icon"
          text={
            <p>
              Icon Tooltips label icons only in instances where the label cannot
              be placed within the main layout. They are triggered on mouseover
              and should be used sparingly.
            </p>
          }
        >
          <div class="c-tooltip--icon c-tooltip--top">
            <div class="c-tooltip__content">Additional Information</div>
          </div>
        </Showcase>
        <hr />
        <SectionTitle>Design Environment</SectionTitle>
        <p>
          Tooltips can be placed as needed to best suit the design: above, below
          or to the left or right of the element they point to. Offer consistent
          visual cues indicating a tooltip is present, either as standalone
          elements (e.g. help or info icons) or as a specific treatment for
          inline elements (e.g. dotted underline on inline text).
          {/* Tooltip
          positioning within the layout is programatically determined by the
          caret's placement in relation to the element it points to. This
          ensures that the tooltip stays in the context of the appropriate page
          item across breakpoints.  */}{' '}
          In order to avoid a tooltip covering related content, they include a
          padding of 8 px between the tooltip caret and the element it points
          to.
        </p>
        <p>
          The information within a tooltip will not scroll, so be careful not to
          overload your tooltip with content. If there is a need for additional
          context, consider using a{' '}
          <Link class="link" to={'/' + data.site + '/patterns/modal/design'}>
            Modal
          </Link>{' '}
          instead.
        </p>

        <SectionTitle>Microcopy</SectionTitle>
        <p>
          Tooltip content can be fragments or full sentences, but should be as
          concise as possible. Only full sentences should use end punctuation.
          Follow the microcopy guidelines for the{' '}
          <Link class="link" to={'/' + data.site + '/components/title/design'}>
            Title
          </Link>{' '}
          component if included.
        </p>
      </Layout>
    </>
  )
}
